/**
 * QueryPanelDataTruncationBanner — inline banner shown below AI prose when a
 * `query_panel_data` tool call returned `truncated: true`.
 *
 * Spec: TDD §3.7 (plans/2026-05-07-track-3-conversational-qa-tdd.md)
 * Brand guide: §36 — muted prose hint treatment consistent with other AI bubbles.
 * Era: dashboard-era only. Track 3 Slice 2.2.
 *
 * Visual contract:
 *  - Small, slightly muted banner immediately below the AI prose (still inside the
 *    chat bubble). Uses `text-secondary` (muted foreground per brand guide §1) so it
 *    reads as context rather than a primary CTA.
 *  - The "→" hint is NOT a clickable link/button — it is prose styled as a hint.
 *    Per TDD §3.7 and CEO OQ-5 (plan): the user types "yes" verbally to trigger the
 *    bridge; the clickable CTA would jump the first confirm gate which is intentional.
 *  - Divider line separates the banner from the AI prose above.
 *
 * Accessibility:
 *  - `role="note"` + `aria-label` for the banner so screen readers distinguish the
 *    metadata from the prose. Same pattern as LibraryGapBanner.
 *  - Banner text is readable at normal font sizes; no icon dependency — plain text
 *    is sufficient for this low-emphasis affordance.
 */

import React from 'react'
import type { QueryPanelDataToolResult } from '@/types/Dashboards.types'

// ─── Props ────────────────────────────────────────────────────────────────────

export interface QueryPanelDataTruncationBannerProps {
  qaToolResult: QueryPanelDataToolResult
}

// ─── Component ────────────────────────────────────────────────────────────────

export const QueryPanelDataTruncationBanner: React.FC<QueryPanelDataTruncationBannerProps> = ({
  qaToolResult,
}) => {
  const { truncated, totalMatched } = qaToolResult

  if (!truncated) return null

  return (
    <div
      className="mt-3 pt-3 border-t border-border/60"
      role="note"
      aria-label="Query result truncated"
      data-testid="qa-truncation-banner"
    >
      <p className="text-xs text-secondary leading-relaxed">
        Showing 50 of {totalMatched} matching records. Add a Table panel to browse all →
      </p>
    </div>
  )
}
